import { useState } from 'react'
import type { JSX } from 'react'
import {
  Badge,
  Dialog,
  Button,
  Flex,
  Heading,
  Loader,
  Text,
  toast,
} from '@buffer-mono/popcorn'
import * as Sentry from '@sentry/react'

import { useStripePaymentConfirmation } from '../../../../hooks/useStripePaymentConfirmation'
import {
  useScaSubscriptionUpdate,
  UpdatePlanPhase,
  SCA_PAYMENT_ERROR_NAME,
} from '../../../../hooks/useScaSubscriptionUpdate'
import type { SubscriptionUpdateResult } from '../../../../hooks/useScaSubscriptionUpdate'
import { BillingIntervalValues } from '../../../PlanSelector/types'
import { useAnnualUpgradeData } from './useAnnualUpgradeData'
import { useAnnualUpgradeTracking } from './useAnnualUpgradeTracking'
import { useUpgradePlanToAnnualBilling } from './useUpgradePlanToAnnualBilling'
import { getBillingPageUrl, redirectToUrl } from '../../../../common/utils/urls'

import styles from './AnnualUpgradeModal.module.css'

interface AnnualUpgradeModalProps {
  onClose: () => void
}

export function AnnualUpgradeModal({
  onClose,
}: AnnualUpgradeModalProps): JSX.Element | null {
  const { data, loading, invoiceLoading, error, refetch } =
    useAnnualUpgradeData()
  const {
    trackSwitchToAnnualClicked,
    trackKeepMonthlyClicked,
    cta,
    upgradePathName,
  } = useAnnualUpgradeTracking()
  const { upgradePlan } = useUpgradePlanToAnnualBilling()
  const paymentConfirmationHook = useStripePaymentConfirmation()
  const [open, setOpen] = useState(true)
  const [updatePhase, setUpdatePhase] = useState(UpdatePlanPhase.Idle)

  const { executeUpdate, isUpdating } = useScaSubscriptionUpdate({
    paymentConfirmationHook,
    updateFn: async () => {
      const response = await upgradePlan({ cta, upgradePathName })
      return response as SubscriptionUpdateResult
    },
    refetch: async () => {
      await refetch()
    },
    callbacks: {
      onPhaseChange: setUpdatePhase,
      onSuccess: () => {
        onClose()
        toast.success("You've switched to annual billing!", {
          action: {
            label: 'View billing',
            onClick: () => {
              redirectToUrl(getBillingPageUrl('annual-upgrade-toast'))
            },
          },
        })
      },
      onError: (err) => {
        // Skip Sentry for SCA failures — already reported by useStripePaymentConfirmation with richer Stripe metadata
        if (err.name !== SCA_PAYMENT_ERROR_NAME) {
          Sentry.captureException(err, {
            tags: {
              component: 'AnnualUpgradeModal',
              action: 'switchToAnnual',
            },
            extra: { planId: data?.planId, quantity: data?.quantity },
          })
        }
        toast.error(
          err.message ||
            'Sorry, there was an error switching to annual billing',
        )
      },
    },
  })

  const isMutating =
    isUpdating ||
    updatePhase === UpdatePlanPhase.Updating ||
    updatePhase === UpdatePlanPhase.Authorizing ||
    updatePhase === UpdatePlanPhase.Confirming

  const handleSwitch = async (): Promise<void> => {
    if (!data) return
    trackSwitchToAnnualClicked()
    await executeUpdate({
      planId: data.planId,
      interval: BillingIntervalValues.year,
      quantity: data.quantity,
    })
  }

  const handleOpenChange = (open: boolean): void => {
    setOpen(open)
    if (!open && !isMutating) {
      trackKeepMonthlyClicked()
      onClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content size="medium" className={styles.dialogContent}>
        <Dialog.CloseButton />
        <Dialog.Body className={styles.body}>
          <Flex direction="column" align="center">
            <Flex direction="column" align="center" gap="xs">
              <Heading as="h3" size="md" align="center">
                Switch to annual billing and save
              </Heading>
              <Text align="center" color="subtle">
                Lock in a lower rate and we&apos;ll credit the time you&apos;ve
                already paid for this month.
              </Text>
            </Flex>

            {loading ? (
              <Flex
                align="center"
                justify="center"
                className={styles.loaderContainer}
              >
                <Loader />
              </Flex>
            ) : error || !data ? (
              <Flex direction="column" align="center" gap="md">
                <Text>Unable to load pricing information.</Text>
                <Button variant="secondary" onClick={() => setOpen(false)}>
                  Close
                </Button>
              </Flex>
            ) : (
              <>
                <Flex direction="column" fullWidth className={styles.cards}>
                  <Flex
                    align="center"
                    justify="between"
                    className={`${styles.card} ${styles.cardMonthly}`}
                  >
                    <Flex direction="column" gap="3xs">
                      <Text weight="bold" color="subtle">
                        Monthly{' '}
                        <span className={styles.currentLabel}>(current)</span>
                      </Text>
                      <Text color="subtle" weight="regular">
                        Paying {data.monthlyPricePerMonth}/mo
                      </Text>
                    </Flex>
                    <Text
                      size="lg"
                      weight="regular"
                      color="subtle"
                      className={styles.cardPrice}
                    >
                      {data.monthlyPricePerYear}/year
                    </Text>
                  </Flex>

                  <Flex
                    align="center"
                    justify="between"
                    className={`${styles.card} ${styles.cardAnnual}`}
                  >
                    <Flex direction="column" gap="3xs">
                      <Text weight="bold">Annual</Text>
                      <Text color="subtle" weight="regular">
                        Billed once a year
                      </Text>
                    </Flex>
                    <Flex align="center" gap="xs">
                      {data.savingsMonths > 0 && (
                        <Badge
                          variant="success"
                          size="small"
                          className={styles.savingsBadge}
                        >
                          <Text
                            as="span"
                            size="sm"
                            weight="medium"
                            color="success"
                          >
                            Save {data.savingsMonths}{' '}
                            {data.savingsMonths === 1 ? 'month' : 'months'} (
                            {data.savingsAmount})
                          </Text>
                        </Badge>
                      )}
                      <Text
                        size="lg"
                        weight="regular"
                        color="default"
                        className={styles.cardPrice}
                      >
                        {data.annualPricePerYear}/year
                      </Text>
                    </Flex>
                  </Flex>

                  {(invoiceLoading || data.dueNow || data.nextBillingDate) && (
                    <Text
                      size="sm"
                      color="subtle"
                      align="center"
                      className={styles.prorationStrip}
                    >
                      {invoiceLoading ? (
                        <span className={styles.amountPlaceholder} />
                      ) : (
                        data.dueNow
                      )}{' '}
                      due today
                      {data.nextBillingDate &&
                        `, renews ${data.nextBillingDate}`}
                    </Text>
                  )}
                </Flex>

                <Flex
                  direction="column"
                  align="center"
                  gap="sm"
                  className={styles.actions}
                >
                  <Button
                    className={styles.actionButton}
                    variant="primary"
                    size="large"
                    onClick={handleSwitch}
                    loading={isMutating}
                    disabled={isMutating || invoiceLoading}
                  >
                    Switch to Annual
                  </Button>
                  <Button
                    className={styles.actionButton}
                    variant="tertiary"
                    onClick={() => setOpen(false)}
                    disabled={isMutating}
                  >
                    Keep Monthly
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  )
}
